/** True if string contains Devanagari script. */
export function hasDevanagari(text) {
  return /[\u0900-\u097F]/.test(String(text || ''));
}

/**
 * Repair UTF-8 Marathi text that was stored/read with the wrong charset (mojibake).
 */
export function fixMojibake(text) {
  const raw = String(text ?? '');
  if (!raw || hasDevanagari(raw)) return raw;

  try {
    const bytes = Uint8Array.from(raw, (ch) => ch.charCodeAt(0) & 0xff);
    const repaired = new TextDecoder('utf-8').decode(bytes);
    if (hasDevanagari(repaired)) return repaired;
  } catch {
    // keep original
  }
  return raw;
}

/** Heuristic: API text that is not Devanagari and looks like encoding corruption. */
export function looksLikeEncodingCorruption(text) {
  const raw = String(text ?? '').trim();
  if (!raw || hasDevanagari(raw)) return false;
  return /[Ãâ€�]|αñ|αÑ|╛|╜|┐|Ø|AcI|I�A/.test(raw);
}

/** Known weapon English → Marathi labels from seed data (fallback only). */
const WEAPON_ENGLISH_TO_MARATHI = {
  talwar: 'तलवार',
  dandpatta: 'दांडपट्टा',
  bhala: 'भाला',
  katyar: 'कट्यार',
  'vagh nakh': 'वाघनखं',
  vaghakh: 'वाघनखं',
  dhal: 'ढाल',
  cannon: 'तोफ',
  musket: 'बंदूक',
  bichwa: 'बिचवा',
  khanjar: 'जांबिया',
  jambiya: 'जांबिया',
};

function marathiFromEnglishName(name) {
  const key = String(name ?? '').trim().toLowerCase();
  return key ? WEAPON_ENGLISH_TO_MARATHI[key] ?? '' : '';
}

/** Marathi-only weapon title — never fall back to English. */
export function getWeaponMarathiName(weaponOrText) {
  if (weaponOrText && typeof weaponOrText === 'object') {
    const mr = getWeaponMarathiName(weaponOrText.marathiNameRaw ?? weaponOrText.marathiName);
    if (mr) return mr;
    return marathiFromEnglishName(weaponOrText.name);
  }
  const fixed = fixMojibake(String(weaponOrText ?? '').trim());
  if (hasDevanagari(fixed) && !looksLikeEncodingCorruption(fixed)) return fixed;
  return '';
}

export function weaponMarathiText(raw) {
  const fixed = fixMojibake(String(raw ?? '').trim());
  if (hasDevanagari(fixed) && !looksLikeEncodingCorruption(fixed)) return fixed;
  return '';
}

/** Prefer Devanagari marathiName; fall back to Latin name when corrupt. */
export function displayMarathiField(marathi, fallback = '') {
  const mr = weaponMarathiText(marathi);
  if (mr) return mr;
  const fb = String(fallback ?? '').trim();
  if (fb && !looksLikeEncodingCorruption(fb)) return fb;
  return '';
}

export function sanitizeMarathiText(text) {
  return displayMarathiField(text, '');
}

/** When API returns duplicate weapons, keep the row with valid Marathi. */
export function dedupeWeaponsPreferMarathi(items) {
  const map = new Map();
  for (const w of items) {
    if (!w) continue;
    const key = String(w.name || w.id).trim().toLowerCase();
    const existing = map.get(key);
    const mr = w.marathiNameRaw ?? w.marathiName ?? '';
    const wValid = hasDevanagari(mr) && !looksLikeEncodingCorruption(mr);
    const eMr = existing?.marathiNameRaw ?? existing?.marathiName ?? '';
    const eValid = existing && hasDevanagari(eMr) && !looksLikeEncodingCorruption(eMr);
    if (!existing || (wValid && !eValid) || (wValid && eValid && w.id < existing.id)) {
      map.set(key, w);
    }
  }
  return [...map.values()];
}

export function filterCorruptWeaponRows(items) {
  return items.filter((w) => Boolean(getWeaponMarathiName(w)));
}
